import {useCallback, useMemo, useState} from 'react';

import {ConnectButtonsRow} from '../../../../../../ui/core-entity/rows/peep-rows/connect-buttons-row';
import type {ConnectButtonsRowProps} from '../../../../../../ui/core-entity/rows/peep-rows/connect-buttons-row';
import type {UserFacingEntity} from '../../../../../../ui/core-entity/rows/peep-rows/types';

import {Stack} from '../../layout/stack/stack';
import type {SelectMenuOption} from '../../select-menu/select-menu';
import {Body} from '../../typography/body';

import {BaseRow} from '../base-row/base-row';
import {getBusinessCardRowToolbarActions} from '../base-row/helpers';
import {RenameableRowTitle} from '../base-row/renameable-row-title';
import {RowValue} from '../base-row/row-value';
import {useDeleteConfirmation} from '../base-row/use-delete-confirmation';
import {useValueSaveToastTracker} from '../base-row/use-value-save-toast-tracker';

import {PhoneRowEditState} from './phone-row-edit-state';

interface Props {
    title?: string;
    isPrimary?: boolean;
    showIsPrimaryIcon?: boolean;
    isFormer?: boolean;
    canMarkAsPrimary?: boolean;
    formattedNumber: string;
    extension: string | null | undefined;
    parentEntity: UserFacingEntity;
    dropdownOptions?: SelectMenuOption[];
    onUpdatePhone: (number: string, extension: string) => Promise<any>;
    onUpdateTitle: (title: string) => Promise<any>;
    onMarkAsPrimary?: () => Promise<any>;
    onChangeFormerStatus?: (isFormer: boolean) => Promise<any>;
    onDelete: () => Promise<any>;
    successFlashKey?: string;
    connectButtonsProps?: ConnectButtonsRowProps;
}

export function PhoneRow(props: Props) {
    const {onUpdatePhone, isFormer, showIsPrimaryIcon = true} = props;

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isMutatingToolbar, setIsMutatingToolbar] = useState<boolean>(false);
    const [isTitleDropdownOpen, setIsTitleDropdownOpen] = useState<boolean>(false);
    const [isCopied, setIsCopied] = useState<boolean>(false);

    const {requestDelete, isDeleteConfirmOpen, deleteConfirmationDialog, deleteAnchorRef} =
        useDeleteConfirmation(props.onDelete, setIsMutatingToolbar, 'phone', props.parentEntity);

    const {valueSaveToastState, setValueSaveToastState, trackValueSaveToastForPromise} =
        useValueSaveToastTracker();

    const handleRowClick = useCallback(() => {
        if (isEditing || isFormer) {
            return;
        }

        setIsEditing(true);
    }, [isEditing, isFormer]);

    const handlePhoneUpdate = useCallback(
        (number: string, extension: string) => {
            return trackValueSaveToastForPromise(
                onUpdatePhone(number, extension).then((result) => {
                    setIsEditing(false);
                    return result;
                })
            );
        },
        [onUpdatePhone, trackValueSaveToastForPromise]
    );

    const toolbarActions = useMemo(() => {
        return getBusinessCardRowToolbarActions(
            {
                isFormer: props.isFormer,
                canMarkAsPrimary: props.canMarkAsPrimary && !props.isPrimary,
                canChangeFormerStatus: !!props.onChangeFormerStatus,
            },
            {
                onMarkAsPrimary: props.onMarkAsPrimary,
                onChangeFormerStatus: props.onChangeFormerStatus,
                onEdit: () => setIsEditing(true),
                onDelete: requestDelete,
                onDeleteRef: deleteAnchorRef,
                onCopy: (isCopied: boolean) => setIsCopied(isCopied),
            },
            `${props.formattedNumber}${props.extension ? ` Ext. ${props.extension}` : ''}`,
            isCopied,
            setIsMutatingToolbar
        );
    }, [
        props.isPrimary,
        props.isFormer,
        props.canMarkAsPrimary,
        props.onMarkAsPrimary,
        props.onChangeFormerStatus,
        requestDelete,
        deleteAnchorRef,
        props.formattedNumber,
        props.extension,
        isCopied,
    ]);

    return (
        <>
            <BaseRow
                type='phone'
                onClick={handleRowClick}
                successFlashKey={props.successFlashKey}
                isPrimary={showIsPrimaryIcon ? props.isPrimary : undefined}
                toolbarProps={{
                    ...toolbarActions,
                    forceShowToolbar: isDeleteConfirmOpen,
                    forceHideToolbar: isEditing || isMutatingToolbar || isTitleDropdownOpen,
                }}
            >
                <RenameableRowTitle
                    title={props.title ?? 'Phone'}
                    options={props.dropdownOptions ?? []}
                    onUpdateTitle={props.onUpdateTitle}
                    saveToastState={valueSaveToastState}
                    setSaveToastState={setValueSaveToastState}
                    onDropdownOpenChange={setIsTitleDropdownOpen}
                    isCopied={isCopied}
                />
                {isEditing ? (
                    <PhoneRowEditState
                        isEditing={isEditing}
                        setIsEditing={setIsEditing}
                        numberFormattedWithCountryCode={props.formattedNumber}
                        extension={props.extension}
                        onChange={handlePhoneUpdate}
                        preventSaveOnBlur={true}
                        ignorePasswordAutoFill={true}
                    />
                ) : (
                    <Stack isHorizontal={true} spacing={4} alignItems='center'>
                        <RowValue value={props.formattedNumber} />
                        {props.extension && (
                            <Body size='tiny' color='light'>
                                Ext: {props.extension}
                            </Body>
                        )}
                    </Stack>
                )}
                {props.connectButtonsProps && !isEditing && !isFormer && (
                    <ConnectButtonsRow
                        id={props.connectButtonsProps.id}
                        phone={props.connectButtonsProps.phone}
                        phoneCallerType={props.connectButtonsProps.phoneCallerType}
                        userCanUseSms={props.connectButtonsProps.userCanUseSms}
                        hasConnectedPhoneIntegration={
                            props.connectButtonsProps.hasConnectedPhoneIntegration
                        }
                        deliverabilityStatus={props.connectButtonsProps.deliverabilityStatus}
                    />
                )}
            </BaseRow>
            {deleteConfirmationDialog}
        </>
    );
}
